import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { probeClient, supabase, verifyPassword } from './supabase';
import type { PermMap } from './roles';
import { withDepartmentName, type DepartmentEmbed } from './departments';

// Password reset never touches the shared client.
//
// It used to: a verified reset code was passed to the MAIN client's verifyOtp,
// which minted a full persisted session, and the only thing standing between
// that session and the workspace was `recovery` — React state backed by a
// localStorage marker. That is a render gate, not a security boundary. Anyone
// holding the mailbox could take the reset code, then either clear the marker
// and reload, or skip the browser entirely and use the token sitting in
// localStorage against PostgREST. The password was never changed and never
// needed to be known.
//
// Now the reset code is redeemed on an in-memory probe client (see
// lib/supabase.ts) whose only job is to carry the new password to updateUser.
// Nothing is persisted, so there is no session to escape into, no marker to
// clear, and a reload simply drops back to the login screen with the code
// spent. After the change the user signs in normally — password AND a fresh
// emailed code — which is the point of having two factors.

export type Profile = {
  id: string;
  full_name: string | null;
  role_id: string | null;
  /** Which department-audience calendar events this person sees. */
  department_id: string | null;
  department: string | null;
  title: string | null;
  avatar_url: string | null;
  location: string | null;
};

type AuthState = {
  session: Session | null;
  profile: Profile | null;
  roleId: string | undefined;
  perms: PermMap;
  loading: boolean;
  configured: boolean;
  // Email-2FA: after a correct password (login) — or just an email (reset) — we
  // email a 6-digit code and hold the user here until they verify it.
  otpEmail: string | null;
  // Why the code was sent: 'login' → straight into the app; 'reset' → on to the
  // set-new-password screen. (Both verify the same way.)
  otpMode: 'login' | 'reset';
  // After a verified reset code (or a legacy PASSWORD_RECOVERY link) the user must
  // set a new password before reaching the app.
  recovery: boolean;
  startEmailOtp: (email: string, password: string) => Promise<{ error?: string }>;
  startResetOtp: (email: string) => Promise<{ error?: string }>;
  verifyEmailOtp: (email: string, token: string) => Promise<{ error?: string }>;
  resendOtp: (email: string) => Promise<{ error?: string }>;
  cancelOtp: () => Promise<void>;
  updatePassword: (newPassword: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  reload: () => Promise<void>;
};

const AuthCtx = createContext<AuthState | undefined>(undefined);

export const useAuth = (): AuthState => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
};

// The department name comes over the department_id FK, never the legacy text
// column (20260917100000_departments.sql); loadFor flattens it back to a string.
const PROFILE_COLS =
  'id, full_name, role_id, department_id, departments(name), title, avatar_url, location';

// The Postgres role a JWT will run as: 'authenticated' for a full session,
// 'auth_pending' for the gate's password-stage and reset-only tokens.
const tokenRole = (jwt: string): string | undefined => {
  try {
    const b64 = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return (JSON.parse(atob(b64)) as { role?: string }).role;
  } catch {
    return undefined;
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [perms, setPerms] = useState<PermMap>({});
  const [loading, setLoading] = useState(true);
  const [otpEmail, setOtpEmail] = useState<string | null>(null);
  const [otpMode, setOtpMode] = useState<'login' | 'reset'>('login');
  const [recovery, setRecovery] = useState(false);
  // Holds the in-memory client that redeemed a reset code, from the moment the
  // code is verified until the new password is saved. A ref, not state: nothing
  // renders off it, and it must not survive a reload.
  const resetClient = useRef<ReturnType<typeof probeClient> | null>(null);
  // Abandoning a reset must not leave a live token in a closure.
  //
  // Every sign-out here is scope 'local' (this device) unless it says
  // otherwise: supabase-js defaults to 'global', which ends the user's sessions
  // on every device (bug report, 2026-10-09). Only a saved new password
  // signs out everywhere.
  const discardResetClient = () => {
    const probe = resetClient.current;
    resetClient.current = null;
    if (probe) void probe.auth.signOut({ scope: 'local' }).catch(() => {});
  };
  const configured = !!supabase;

  // Fetch the profile + the live permission map for its role.
  const loadFor = useCallback(async (userId: string): Promise<Profile | null> => {
    if (!supabase) return null;
    const { data: prof, error } = await supabase
      .from('profiles')
      .select(PROFILE_COLS)
      .eq('id', userId)
      .single();
    if (error) console.error('Failed to load profile:', error.message);
    const p: Profile | null = prof
      ? withDepartmentName(prof as unknown as Omit<Profile, 'department'> & DepartmentEmbed)
      : null;
    if (p?.role_id) {
      const { data: rows } = await supabase
        .from('role_permissions')
        .select('module, level')
        .eq('role_id', p.role_id);
      const map: PermMap = {};
      (rows ?? []).forEach((r: { module: string; level: string }) => {
        map[r.module] = r.level as PermMap[string];
      });
      setPerms(map);
    } else {
      setPerms({});
    }
    setProfile(p);
    return p;
  }, []);

  const reload = useCallback(async () => {
    if (session?.user.id) await loadFor(session.user.id);
  }, [session, loadFor]);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    const sb = supabase; // bound so the callbacks below narrow past the null check
    sb.auth.getSession().then(({ data }) => {
      setSession(data.session);
      // No mid-reset case to restore: a reset in flight owns no persisted
      // session, so a reload lands on the login screen by construction.
      if (!data.session) setLoading(false);
    });
    const { data: sub } = sb.auth.onAuthStateChange((event, s) => {
      // Nothing calls resetPasswordForEmail, so this only fires if someone
      // re-enables link-based recovery. That path hands the shared client a
      // real session before any password is set — the exact hole closed above —
      // so refuse it instead of gating it with a flag. Re-enabling links means
      // routing them through a probe client too.
      if (event === 'PASSWORD_RECOVERY') {
        void sb.auth.signOut();
        return;
      }
      setSession(s);
      if (!s) {
        setProfile(null);
        setPerms({});
        setLoading(false);
      } else if (event !== 'TOKEN_REFRESHED') {
        setLoading(true); // gate routing until the profile/role loads
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Load profile + perms whenever the session changes (skip while a 2FA code is pending).
  useEffect(() => {
    if (!session || !supabase) return;
    let active = true;
    loadFor(session.user.id).then(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [session, loadFor]);

  // Live: if this user's role's permissions change, refresh the map (no reload).
  useEffect(() => {
    if (!supabase || !profile?.role_id) return;
    const sb = supabase;
    const channel = sb
      .channel(`perms:${profile.role_id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'role_permissions',
          filter: `role_id=eq.${profile.role_id}`,
        },
        () => {
          void reload();
        },
      )
      .subscribe();
    return () => {
      void sb.removeChannel(channel);
    };
  }, [profile?.role_id, reload]);

  // Step 1 of login: verify the password on a throwaway, non-persisting client,
  // then email a 6-digit code. The two-factor rule itself lives on the server
  // (the auth_gate hook): this step's token reads nothing and dies in 60 s, and
  // it records the password so the code that follows can mint a real session.
  const startEmailOtp = async (email: string, password: string) => {
    if (!supabase) return { error: 'Auth is not configured.' };
    const { error: pwErr } = await verifyPassword(email, password);
    if (pwErr) return { error: pwErr };
    setOtpMode('login');
    setOtpEmail(email);
    const { error: otpErr } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    });
    if (otpErr) {
      setOtpEmail(null);
      return { error: otpErr.message };
    }
    return {};
  };

  // Forgot password: prove email ownership with a code instead of a reset link
  // (no redirect, no Site-URL dependency). A verified code then unlocks the
  // set-new-password screen. Uses the same email-OTP as login.
  const startResetOtp = async (email: string) => {
    if (!supabase) return { error: 'Auth is not configured.' };
    // Restarting a reset must not orphan the previous attempt's live token.
    discardResetClient();
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    });
    if (error) return { error: error.message };
    setOtpMode('reset');
    setOtpEmail(email);
    return {};
  };

  // Step 2: the code creates a real session. For a login that's the app; for a
  // reset we route to the new-password screen first.
  const verifyEmailOtp = async (email: string, token: string) => {
    if (!supabase) return { error: 'Auth is not configured.' };

    // Reset: redeem the code on a throwaway client so the shared one stays
    // signed out. The session that comes back can do exactly one useful thing —
    // updateUser({ password }) — and it dies with the tab.
    if (otpMode === 'reset') {
      const probe = probeClient();
      const { error } = await probe.auth.verifyOtp({ email, token, type: 'email' });
      if (error) return { error: error.message };
      resetClient.current = probe;
      setRecovery(true);
      setOtpEmail(null);
      setOtpMode('login');
      return {};
    }

    const { data, error } = await supabase.auth.verifyOtp({ email, token, type: 'email' });
    if (error) return { error: error.message };
    // The server (20261010110000_auth_gate.sql) mints a full session only when
    // this code follows a password checked in the last 5 minutes. Otherwise the
    // token is reset-only and opens nothing, so don't land in an empty workspace.
    if (data.session && tokenRole(data.session.access_token) !== 'authenticated') {
      await supabase.auth.signOut({ scope: 'local' });
      return {
        error: 'This code came more than 5 minutes after your password. Go back and sign in again.',
      };
    }
    setOtpEmail(null);
    setOtpMode('login');
    return {};
  };

  const resendOtp = async (email: string) => {
    if (!supabase) return { error: 'Auth is not configured.' };
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false },
    });
    return { error: error?.message };
  };

  const cancelOtp = async () => {
    setOtpEmail(null);
    setOtpMode('login');
    discardResetClient();
    await supabase?.auth.signOut({ scope: 'local' });
  };

  const updatePassword = async (newPassword: string) => {
    if (!supabase) return { error: 'Auth is not configured.' };

    // Mid-reset: the only session that can authorise this is the probe's, and
    // it is discarded the moment the password is saved. The user then signs in
    // through the normal two-factor path — which is what proves the reset
    // actually happened, rather than a flag claiming it did.
    const probe = resetClient.current;
    if (probe) {
      const { error } = await probe.auth.updateUser({ password: newPassword });
      if (error) return { error: error.message };
      // Global on purpose: a new password ends every device's session.
      await probe.auth.signOut().catch(() => {});
      resetClient.current = null;
      // `recovery` deliberately stays true. There is no session now, so clearing
      // it here would unmount ResetPasswordScreen and drop the user straight
      // onto the login form with no word on whether the reset worked. Its
      // "Password updated → Continue to sign in" panel calls signOut, which is
      // what clears the flag.
      return {};
    }

    // Signed-in change from the Security screen.
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { error: error.message };
    return {};
  };

  const signOut = async () => {
    setOtpEmail(null);
    setOtpMode('login');
    discardResetClient();
    setRecovery(false);
    await supabase?.auth.signOut({ scope: 'local' }); // this device only (owner, 2026-11-03)
  };

  return (
    <AuthCtx.Provider
      value={{
        session,
        profile,
        roleId: profile?.role_id ?? undefined,
        perms,
        loading,
        configured,
        otpEmail,
        otpMode,
        recovery,
        startEmailOtp,
        startResetOtp,
        verifyEmailOtp,
        resendOtp,
        cancelOtp,
        updatePassword,
        signOut,
        reload,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}
